import React from 'react';
import type { EacoDocumentContent } from '@eaco/forms-core';
import type { Updater } from 'use-immer';
import { cn } from '../lib/utils';
import DocumentView from './DocumentView';
import FormBrandHeader from './FormBrandHeader';
import FillDesktopFrame from './FillDesktopFrame';
import { FillSubmitButton, FILL_ACTION_BAR_CLASS, FILL_ACTION_BAR_NARROW_CLASS, FILL_ACTION_BAR_ATTR } from './FillSubmitButton';
import { usePreviewFormContext } from './PreviewFormContext';

interface Props {
  eacoDocument: EacoDocumentContent;
  values: Record<string, unknown>;
  updateValues: Updater<Record<string, unknown>>;
}

/**
 * Fill chrome for a **pageless** form (no top-level Pages stepper). On
 * desktop it wears the same two-column shell as a paged form — but the rail
 * carries **only the brand** (logo + form + company name); the rest is
 * intentionally empty so paged and pageless forms look like one product.
 * The answer column has no step header (the brand already names the form),
 * scrolls the fields, and pins a single Submit in the footer.
 *
 * Below the breakpoint it reproduces today's layout exactly: brand band, the
 * flat field stack, and a sticky Submit bar.
 */
export const PagelessFillFrame: React.FC<Props> = ({
  eacoDocument,
  values,
  updateValues,
}) => {
  const ctx = usePreviewFormContext();
  const canSubmit = !!ctx?.onSubmit;
  const submitting = !!ctx?.submitting;

  const doc = (
    <DocumentView
      eacoDocument={eacoDocument}
      values={values}
      updateValues={updateValues}
    />
  );

  // The host owns the final Submit control on public fills (Turnstile popover);
  // fall back to the plain button in preview / editor contexts.
  const submitButton = ctx?.submitSlot ?? (
    canSubmit ? (
      <FillSubmitButton
        onClick={() => void ctx?.onSubmit?.()}
        submitting={submitting}
        label={ctx?.submitLabel}
      />
    ) : null
  );

  return (
    <FillDesktopFrame
      brand={<FormBrandHeader variant="rail" />}
      // rail + progress intentionally empty for a pageless form.
      rail={null}
      progress={null}
      // No step header — the brand names the form.
      header={null}
      body={doc}
      footer={
        submitButton ? (
          <div className="flex items-center gap-[10px] px-[20px] py-[12px]">
            <div className="flex-1" />
            {submitButton}
          </div>
        ) : null
      }
      narrow={
        <div className="flex min-h-full flex-1 flex-col">
          {/* Pinned, so the save status in the band stays in view down a long form. */}
          <div className="sticky top-0 z-[6]" data-fill-pinned-top="">
            <FormBrandHeader variant="band" />
          </div>
          <div className="flex-1 space-y-6 p-[18px]">{doc}</div>
          {submitButton && (
            <div
              className={cn(FILL_ACTION_BAR_CLASS, FILL_ACTION_BAR_NARROW_CLASS)}
              {...FILL_ACTION_BAR_ATTR}
            >
              {submitButton}
            </div>
          )}
        </div>
      }
    />
  );
};

export default PagelessFillFrame;
